import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import AuthContext from '../../contexts/AuthContext';
import api from '../../api/api';
import '../../styles/ChildAssignmentsPage.css';

const ChildAssignmentsPage = () => {
  const { childId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [childName, setChildName] = useState(location.state?.childName || '');

  // Determine effective childId from params or location state
  const effectiveChildId = childId || location.state?.childId;

  useEffect(() => {
    if (!effectiveChildId) {
      setError('No child ID provided');
      setLoading(false);
      return;
    }
    
    fetchAssignments();
    
    // If we don't have the child's name, fetch it
    if (!childName) {
      fetchChildDetails();
    }
  }, [effectiveChildId]);

  // Fetch child details to get name
  const fetchChildDetails = async () => {
    try {
      const response = await api.get(`/users/${effectiveChildId}`);
      if (response.data) {
        setChildName(response.data.name || 'Child');
      }
    } catch (err) {
      console.error('Error fetching child details:', err);
    }
  };

  // Fetch assignments for the child
  const fetchAssignments = async () => {
    try {
      setLoading(true);
      
      // Use the parent-specific endpoint
      const response = await api.get(`/parents/children/${effectiveChildId}/assignments`);
      
      if (response.data && response.data.data) {
        console.log('Child assignments:', response.data.data);
        
        // Log date fields for completed assignments to debug
        const completedAssignments = response.data.data.filter(a => a.status === 'completed');
        if (completedAssignments.length > 0) {
          console.log('Completed assignments date fields:');
          completedAssignments.forEach(assignment => {
            console.log(`Assignment ID ${assignment._id}:`, {
              completedDate: assignment.completedDate,
              updatedAt: assignment.updatedAt,
              createdAt: assignment.createdAt,
              assessedAt: assignment.assessment?.assessedAt,
              assessmentDate: assignment.assessment?.date
            });
          });
        }
        
        setAssignments(response.data.data);
      } else {
        setError('Failed to load assignments');
      }
    } catch (err) {
      console.error('Error fetching assignments:', err);
      setError('Failed to load assignments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter assignments based on selected filter
  const getFilteredAssignments = () => {
    if (activeFilter === 'all') return assignments;
    
    if (activeFilter === 'active') {
      return assignments.filter(assignment => 
        assignment.status === 'active' || 
        (assignment.assessment && assignment.assessment.rating === 'Need to Repeat')
      );
    }
    
    if (activeFilter === 'completed') {
      return assignments.filter(assignment => 
        assignment.status === 'completed' && 
        (!assignment.assessment || assignment.assessment.rating !== 'Need to Repeat')
      );
    }
    
    if (activeFilter === 'new-memorization' || activeFilter === 'revision') {
      return assignments.filter(assignment => 
        assignment.type === activeFilter
      );
    }
    
    return assignments;
  };

  // Navigate to assignment details
  const navigateToAssignment = (assignment) => {
    navigate(`/assignments/${assignment._id}`, {
      state: {
        fromParentChildView: true,
        childId: effectiveChildId,
        childName: childName
      }
    });
  };

  // Format date for display
  const formatDate = (dateString, isCompletionDate = false) => {
    if (!dateString) return isCompletionDate ? 'Unknown date' : 'No due date';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return isCompletionDate ? 'Unknown date' : 'No due date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return isCompletionDate ? 'Unknown date' : 'No due date';
    }
  };

  if (loading) {
    return (
      <div className="child-assignments-loading">
        <div className="loading-spinner"></div>
        <p>Loading assignments...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="child-assignments-error">
        <p>{error}</p>
        <button onClick={() => navigate('/dashboard')}>
          Return to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="child-assignments-container">
      <div className="child-assignments-header">
        <Link to={`/parent/children/${effectiveChildId}`} className="back-link">
          ← Back to Child Profile
        </Link>
        <h1>{childName}'s Assignments</h1>
      </div>
      
      {/* Filter options */}
      <div className="filter-bar">
        <div className="filter-chips">
          <button 
            className={`filter-chip ${activeFilter === 'all' ? 'filter-chip-active' : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            All
          </button>
          
          <button 
            className={`filter-chip ${activeFilter === 'active' ? 'filter-chip-active' : ''}`}
            onClick={() => setActiveFilter('active')}
          >
            Active
          </button>
          
          <button 
            className={`filter-chip ${activeFilter === 'completed' ? 'filter-chip-active' : ''}`}
            onClick={() => setActiveFilter('completed')}
          >
            Completed
          </button>
          
          <button 
            className={`filter-chip ${activeFilter === 'new-memorization' ? 'filter-chip-active' : ''}`}
            onClick={() => setActiveFilter('new-memorization')}
          >
            New Memorization
          </button>
          
          <button 
            className={`filter-chip ${activeFilter === 'revision' ? 'filter-chip-active' : ''}`}
            onClick={() => setActiveFilter('revision')}
          >
            Revision
          </button>
        </div>
      </div>
      
      {/* Assignments list */}
      <div className="assignments-list">
        {getFilteredAssignments().length > 0 ? (
          <>
            <div className="assignments-count">
              Found {getFilteredAssignments().length} assignments
            </div>
            
            {getFilteredAssignments().map(assignment => (
              <div 
                key={assignment._id} 
                className={`assignment-card ${assignment.status === 'completed' ? 'completed' : ''}`}
                onClick={() => navigateToAssignment(assignment)}
              >
                <div className="assignment-type-badge">
                  {assignment.type === 'new-memorization' ? 'New Memorization' : 'Revision'}
                </div>
                
                <h3 className="assignment-title">
                  {assignment.surah?.name || 'Unknown Surah'} {assignment.verses ? `(${assignment.verses.from}-${assignment.verses.to})` : ''}
                </h3>
                
                <div className="assignment-due-date">
                  {assignment.status === 'completed' ? (
                    `Completed on ${formatDate(assignment.completedDate || assignment.assessment?.assessedAt || assignment.updatedAt, true)}`
                  ) : (
                    `Due: ${formatDate(assignment.dueDate)}`
                  )}
                </div>
                
                <div className={`assignment-status ${
                  assignment.assessment && assignment.assessment.rating && 
                  (assignment.assessment.rating.toLowerCase().includes('need') || 
                   assignment.assessment.rating.toLowerCase().includes('improvement')) 
                    ? 'assessment-needs-improvement' 
                    : assignment.assessment ? 'assessment-good' : ''
                }`}>
                  {assignment.assessment && assignment.assessment.rating
                    ? `Assessment: ${assignment.assessment.rating}` 
                    : 'Status: Pending Assessment'}
                </div>
                
                {assignment.assessment && assignment.assessment.comments && (
                  <div className="assignment-comments">
                    <span className="comments-label">Teacher Comments:</span>
                    <p className="comments-text">"{assignment.assessment.comments}"</p>
                  </div>
                )}
              </div>
            ))}
          </>
        ) : (
          <div className="empty-assignments">
            <p>No assignments found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChildAssignmentsPage;